// ========================================
// COMPOSABLE: useDragAndDrop
// ========================================
// Composable reutilizável para funcionalidade de drag and drop
// Compatível com Vue 3 Composition API

import { ref } from 'vue';
import { notify } from 'sysweb-vue-components/src/global';

/**
 * Composable para implementar drag and drop em listas
 * @param {Object} options - Opções de configuração
 * @param {Array} options.items - Array de items para reordenar
 * @param {Function} options.onReorder - Callback chamado quando items são reordenados
 * @param {Object} options.messages - Mensagens personalizadas
 * @param {Object} options.dragImage - Configurações da imagem de drag
 * @returns {Object} - Objeto com estados e funções para drag and drop
 */
export function useDragAndDrop(options = {}) {
    // Configurações padrão
    const defaultOptions = {
        items: [],
        onReorder: null,
        messages: {
            success: 'Ordem alterada com sucesso!',
            error: 'Erro ao alterar ordem'
        },
        dragImage: {
            opacity: 0.8,
            rotation: 3,
            scale: 0.95
        },
        ...options
    };

    // Estados reativos do drag and drop
    const draggingIndex = ref(null);
    const dragOverIndex = ref(null);
    const itemOrder = ref([]);

    // Inicializar ordem dos items
    const initializeOrder = (items) => {
        itemOrder.value = items.map((_, index) => index);
    };

    // Obter items na ordem atual
    const getSortedItems = (items) => {
        if (itemOrder.value.length === 0 && items.length > 0) {
            initializeOrder(items);
        }
        return itemOrder.value.map(index => items[index]).filter(Boolean);
    };

    // Função para iniciar o drag
    const onDragStart = (event, index) => {
        draggingIndex.value = index;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/html', event.target);

        // Criar imagem de drag personalizada
        const dragElement = event.currentTarget.cloneNode(true);
        dragElement.style.position = 'absolute';
        dragElement.style.top = '-1000px';
        dragElement.style.left = '-1000px';
        dragElement.style.width = event.currentTarget.offsetWidth + 'px';
        dragElement.style.opacity = defaultOptions.dragImage.opacity;
        dragElement.style.transform = `rotate(${defaultOptions.dragImage.rotation}deg)`;
        dragElement.style.pointerEvents = 'none';
        dragElement.style.zIndex = '9999';

        document.body.appendChild(dragElement);

        // Calcular offset correto do cursor
        const rect = event.currentTarget.getBoundingClientRect();
        const offsetX = event.clientX - rect.left;
        const offsetY = event.clientY - rect.top;

        event.dataTransfer.setDragImage(dragElement, offsetX, offsetY);

        // Remover elemento temporário
        setTimeout(() => {
            if (document.body.contains(dragElement)) {
                document.body.removeChild(dragElement);
            }
        }, 1);
    };

    // Função para permitir drag over
    const onDragOver = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    // Função para entrar na área de drop
    const onDragEnter = (event, index) => {
        event.preventDefault();
        dragOverIndex.value = index;
    };

    // Função para sair da área de drop
    const onDragLeave = (event) => {
        // Só remove o highlight se realmente saiu do elemento
        if (!event.currentTarget.contains(event.relatedTarget)) {
            dragOverIndex.value = null;
        }
    };

    // Função para realizar o drop
    const onDrop = (event, targetIndex) => {
        event.preventDefault();

        if (draggingIndex.value === null || draggingIndex.value === targetIndex) {
            return;
        }

        // Reorganizar a ordem dos items
        const newOrder = [...itemOrder.value];
        const draggedItem = newOrder[draggingIndex.value];

        // Remove o item da posição original
        newOrder.splice(draggingIndex.value, 1);

        // Insere na nova posição
        newOrder.splice(targetIndex, 0, draggedItem);

        itemOrder.value = newOrder;

        // Chamar callback de reordenação se fornecido
        if (defaultOptions.onReorder && typeof defaultOptions.onReorder === 'function') {
            try {
                defaultOptions.onReorder(newOrder, {
                    from: draggingIndex.value,
                    to: targetIndex,
                    item: draggedItem
                });
            } catch (error) {
                console.error('Erro no callback onReorder:', error);
                notify(defaultOptions.messages.error, 'error');
                return;
            }
        }

        // Mostrar mensagem de sucesso
        notify(defaultOptions.messages.success, 'success');

        console.log('Items reordenados:', newOrder);
    };

    // Função para finalizar o drag
    const onDragEnd = () => {
        draggingIndex.value = null;
        dragOverIndex.value = null;
    };

    // Função para resetar a ordem
    const resetOrder = () => {
        itemOrder.value = [];
        draggingIndex.value = null;
        dragOverIndex.value = null;
    };

    // Função para definir ordem personalizada
    const setCustomOrder = (newOrder) => {
        if (Array.isArray(newOrder)) {
            itemOrder.value = [...newOrder];
        }
    };

    // Função para verificar se está arrastando
    const isDragging = (index) => {
        return draggingIndex.value === index;
    };

    // Função para verificar se está sobre o elemento
    const isDragOver = (index) => {
        return dragOverIndex.value === index && draggingIndex.value !== index;
    };

    // Retornar API pública do composable
    return {
        // Estados
        draggingIndex,
        dragOverIndex,
        itemOrder,

        // Funções principais
        onDragStart,
        onDragOver,
        onDragEnter,
        onDragLeave,
        onDrop,
        onDragEnd,

        // Utilitários
        getSortedItems,
        initializeOrder,
        resetOrder,
        setCustomOrder,
        isDragging,
        isDragOver,

        // Classes CSS para aplicar nos elementos
        getDragClasses: (index) => ({
            'dragging': isDragging(index),
            'drag-over': isDragOver(index)
        })
    };
}

// ========================================
// EXEMPLO DE USO
// ========================================
/*
// No componente Vue:

<template>
    <div class="drag-list">
        <div
            v-for="(item, index) in sortedItems"
            :key="item.id"
            :class="getDragClasses(index)"
            draggable="true"
            @dragstart="onDragStart($event, index)"
            @dragover="onDragOver($event)"
            @dragenter="onDragEnter($event, index)"
            @dragleave="onDragLeave($event)"
            @drop="onDrop($event, index)"
            @dragend="onDragEnd"
        >
            <div class="drag-handle">⋮⋮</div>
            <div class="item-content">{{ item.name }}</div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useDragAndDrop } from '@/assets/js/dragdrop/useDragAndDrop';

const items = ref([
    { id: 1, name: 'Item 1' },
    { id: 2, name: 'Item 2' },
    { id: 3, name: 'Item 3' }
]);

const {
    onDragStart,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onDrop,
    onDragEnd,
    getSortedItems,
    getDragClasses
} = useDragAndDrop({
    items: items.value,
    onReorder: (newOrder, details) => {
        console.log('Items reordenados:', newOrder, details);
        // Aqui você pode salvar no backend, etc.
    },
    messages: {
        success: 'Lista reordenada!',
        error: 'Erro ao reordenar'
    }
});

const sortedItems = computed(() => getSortedItems(items.value));
</script>

<style scoped>
.drag-list > div {
    transition: all 0.3s ease;
    cursor: grab;
}

.drag-list > div:active {
    cursor: grabbing;
}

.drag-list > div.dragging {
    opacity: 0.3;
    transform: scale(0.95);
}

.drag-list > div.drag-over {
    border: 2px solid #3b82f6;
    transform: translateY(-2px);
}

.drag-handle {
    cursor: grab;
    user-select: none;
}
</style>
*/